import { Box, Card, Flex } from '@mantine/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Layout, Responsive, ResponsiveProps, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNavigate, useParams } from 'react-router';
import {
  useCreateComponent,
  useCreateDataEntryComponent,
  useCreatePage,
  useDeleteComponent,
  useDeleteDataEntryComponent,
  useDeletePage,
  usePages,
  useUpdateComponent,
  useUpdateDataEntryComponent,
  useUpdatePage,
} from '../../api/pages-client/pages-client.hooks';
import type { Component, DataEntryComponent, Page, PageWritePayload } from '../../api/pages-client/pages-client.type';
import i18n from '../../base-dictionary';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { PageHeaderMessage } from '../../components/page-header-message/page-header-message';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useGlobalStore } from '../../store/global-store/global-store';
import { primaryBase } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';
import { DeleteConfirmationModal } from '../data-management/components/delete-confirmation-modal';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { AddWidgetModal } from './components/add-widget-modal';
import { DashboardActions } from './components/dashboard-actions';
import { DashboardHeader } from './components/dashboard-header';
import { DashboardInfoCard } from './components/dashboard-info-card';
import { EditDashboardModal } from './components/dashboard-modal';
import { DashboardWidget } from './components/dashboard-widget';
import { DataEntryWidget } from './components/data-entry-widget/data-entry-widget';
import { DataEntryWidgetModal } from './components/data-entry-widget/data-entry-widget-modal';
import {
  calculateWidgetLayout,
  createDefaultLayout,
  layoutDataToLayout,
  layoutToLayoutData,
} from './dashboards.helpers';

const ResponsiveGridLayout = WidthProvider(Responsive) as React.ComponentType<ResponsiveProps>;

export const Dashboard: FC = () => {
  useDocumentTitle('Dashboards - Bloomfilter');
  const { dashboardId } = useParams();
  const navigate = useNavigate();

  const [message, setMessage] = useState<string | null>(null);
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [isAddDataEntryWidgetOpen, setIsAddDataEntryWidgetOpen] = useState(false);
  const [isEditDashboardOpen, setIsEditDashboardOpen] = useState(false);
  const [isCreateDashboardOpen, setIsCreateDashboardOpen] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(dashboardId || '');
  const [localDashboards, setLocalDashboards] = useState<Page[]>([]);
  const [isDeleteDashboardOpen, setIsDeleteDashboardOpen] = useState(false);

  // Add state for layout editing mode
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);

  // Add state to track current layout changes
  const [currentLayout, setCurrentLayout] = useState<Layout[]>([]);
  // Add state to store the original layout before editing
  const [originalLayout, setOriginalLayout] = useState<Layout[]>([]);

  // Get organization and portfolio IDs from global store
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);

  // biome-ignore lint/correctness/useExhaustiveDependencies: portfolio id needed to reset dashboards on portfolio change
  useEffect(() => {
    setSelectedDashboard('');
    setLocalDashboards([]);
  }, [portfolio?.id]);

  const { pages, query } = usePages(
    organization?.id || '',
    portfolio?.id || '',
    'dashboard', // type
    'dashboard', // moduleType
  );

  // Keep local state in sync with server data
  useEffect(() => {
    if (!query.isLoading && !query.isFetching && pages) {
      const pagesArray = pages || [];
      // Only update local dashboards if they've actually changed
      if (JSON.stringify(localDashboards) !== JSON.stringify(pagesArray)) {
        setLocalDashboards(pagesArray);
      }

      // Handle dashboard selection
      if (dashboardId) {
        // If URL has a dashboard ID, use that
        if (selectedDashboard !== dashboardId) {
          setSelectedDashboard(dashboardId);
        }
      } else if (pagesArray.length > 0 && !selectedDashboard) {
        // If no dashboard is selected but we have dashboards, select the first one and update URL
        const firstDashboardId = pagesArray[0].id;
        if (firstDashboardId) {
          setSelectedDashboard(firstDashboardId);
          navigate(`/application/dashboards/${firstDashboardId}`, { replace: true });
        }
      }
    }
  }, [pages, query.isLoading, query.isFetching, localDashboards, selectedDashboard, dashboardId, navigate]);

  const selectedPage = useMemo(() => {
    const result = selectedDashboard ? localDashboards.find((page: Page) => page.id === selectedDashboard) : null;
    return result;
  }, [selectedDashboard, localDashboards]);

  const createPageMutation = useCreatePage();
  const createComponentMutation = useCreateComponent();
  const createDataEntryComponentMutation = useCreateDataEntryComponent();
  const updateDataEntryComponentMutation = useUpdateDataEntryComponent();
  const deleteDataEntryComponentMutation = useDeleteDataEntryComponent();
  const deletePageMutation = useDeletePage();
  const updatePageMutation = useUpdatePage();
  const deleteComponentMutation = useDeleteComponent();
  const updateComponentMutation = useUpdateComponent();

  const pruneColorMap = (colorMap: Record<string, string>, objectIds: string[]) => {
    return Object.fromEntries(Object.entries(colorMap).filter(([key]) => objectIds.includes(key)));
  };

  const handleAddWidget = async (widgetData: Component) => {
    try {
      if (selectedPage && selectedPage.id) {
        // Add widget to existing page using createComponent
        const newComponent = await createComponentMutation.mutateAsync({
          component: {
            name: widgetData.name,
            description: widgetData.description || '',
            type: widgetData.type,
            sub_type: widgetData.sub_type,
            show_title: widgetData.show_title,
            page: selectedPage.id,
            component_data: widgetData.component_data?.map((data) => ({
              measure: data.measure,
              range_type: data.range_type,
              object_type: data.object_type,
              object_ids: data.object_ids,
              color_map: data.color_map,
              time_allocation_type: data.time_allocation_type,
            })),
          },
        });

        // Update local state after successful API call
        setLocalDashboards((prev) =>
          prev.map((page) =>
            page.id === selectedPage.id
              ? {
                  ...page,
                  components: [...(page.components || []), newComponent],
                }
              : page,
          ),
        );
        // Refetch data to ensure we have the latest state
        query.refetch();
      } else {
        // Create new page with widget
        const newPage: PageWritePayload = {
          name: 'New Dashboard',
          description: 'New dashboard with widget',
          type: 'dashboard',
          layout: 'grid',
          components: [], // Start with empty components, we'll add the widget after creation
          organization: organization?.id || '',
          portfolio: portfolio?.id || '',
          module_type: 'dashboard',
          page_template: null,
          page_template_inputs: {},
        };
        const createdPage = await createPageMutation.mutateAsync(newPage);

        // Add the widget to the new page
        const newComponent = await createComponentMutation.mutateAsync({
          component: {
            name: widgetData.name,
            page: createdPage.id || '',
            description: widgetData.description || '',
            type: widgetData.type,
            sub_type: widgetData.sub_type,
            show_title: widgetData.show_title,
            component_data: widgetData.component_data?.map((data) => {
              const prunedColorMap = data.color_map ? pruneColorMap(data.color_map, data.object_ids) : data.color_map;
              return {
                measure: data.measure,
                range_type: data.range_type,
                object_type: data.object_type,
                object_ids: data.object_ids,
                color_map: prunedColorMap,
                time_allocation_type: data.time_allocation_type,
              };
            }),
          },
        });

        // Update local state after successful API calls
        setLocalDashboards([
          ...localDashboards,
          {
            ...createdPage,
            components: [newComponent],
          },
        ]);

        // Navigate to the new dashboard instead of directly setting the state
        navigate(`/application/dashboards/${createdPage.id}`);

        // Refetch data to ensure we have the latest state
        query.refetch();
      }
      setIsAddWidgetOpen(false);
    } catch (error) {
      console.error('Failed to add widget:', error);
    }
  };

  const handleAddDataEntryWidget = async (widgetData: DataEntryComponent) => {
    try {
      if (selectedPage && selectedPage.id) {
        // Add widget to existing page using createDataEntryComponent
        const newComponent = await createDataEntryComponentMutation.mutateAsync({
          payload: {
            name: widgetData.name,
            description: widgetData.description || '',
            page: selectedPage.id,
            column_data: widgetData.column_data,
            layout_data: widgetData.layout_data,
            stored_data: widgetData.stored_data,
          },
        });

        setLocalDashboards((prev) =>
          prev.map((page) =>
            page.id === selectedDashboard
              ? { ...page, data_entry_components: [...(page.data_entry_components || []), newComponent] }
              : page,
          ),
        );
        query.refetch();
      }
    } catch (error) {
      console.error('Failed to add data entry widget:', error);
    }
    setIsAddDataEntryWidgetOpen(false);
  };

  const handleEditDataEntryWidget = async (updatedWidget: DataEntryComponent) => {
    try {
      const updatedComponent = await updateDataEntryComponentMutation.mutateAsync({
        id: updatedWidget.id!,
        payload: updatedWidget,
      });

      setLocalDashboards((prev) =>
        prev.map((page) =>
          page.id === selectedDashboard
            ? {
                ...page,
                data_entry_components: page.data_entry_components?.map((comp) =>
                  comp.id === updatedWidget.id ? updatedComponent : comp,
                ),
              }
            : page,
        ),
      );
      query.refetch();
    } catch (error) {
      console.error('Failed to update data entry widget:', error);
    }
  };

  const handleDeleteDataEntryWidget = async (widgetId: string) => {
    try {
      await deleteDataEntryComponentMutation.mutateAsync({ id: widgetId });

      setLocalDashboards((prev) =>
        prev.map((page) =>
          page.id === selectedPage?.id
            ? { ...page, data_entry_components: page.data_entry_components?.filter((comp) => comp.id !== widgetId) }
            : page,
        ),
      );
      query.refetch();
    } catch (error) {
      console.error('Failed to delete data entry widget:', error);
    }
  };

  const handleEditDashboard = async (data: { name: string; description: string }) => {
    if (!selectedPage || !organization?.id) return;

    try {
      // Ensure all components have required fields
      const components: Component[] = (selectedPage.components || []).map(
        (comp) =>
          ({
            id: comp.id,
            name: comp.name,
            page: selectedPage.id,
            description: comp.description || '',
            type: comp.type,
            sub_type: comp.sub_type || 'line',
            show_title: comp.show_title,
            component_data: comp.component_data?.map((data) => {
              const prunedColorMap = data.color_map ? pruneColorMap(data.color_map, data.object_ids) : data.color_map;
              return {
                id: data.id || '',
                measure: data.measure,
                range_type: data.range_type,
                object_type: data.object_type,
                object_ids: data.object_ids,
                unit: 'unit' in data ? data.unit : undefined,
                color_map: prunedColorMap,
              };
            }),
            layout_data: comp.layout_data,
          }) as Component,
      );
      const updatedPage: Page = {
        id: selectedPage.id,
        name: data.name,
        description: data.description,
        type: selectedPage.type,
        layout: 'grid',
        organization: organization.id || '',
        portfolio: portfolio?.id || '',
        module_type: 'dashboard',
        components,
        page_template: selectedPage.page_template,
        data_entry_components: selectedPage.data_entry_components,
      };

      // First argument is id, second is the page data
      const result = await updatePageMutation.mutateAsync({ id: selectedPage.id, payload: updatedPage });

      // Update local state after successful API call
      setLocalDashboards(localDashboards.map((page) => (page.id === selectedDashboard ? result : page)));
      // Refetch data to ensure we have the latest state
      query.refetch();
      setIsEditDashboardOpen(false);
    } catch (error) {
      console.error('Failed to update dashboard:', error);
    }
  };

  const handleCreateDashboard = async (data: { name: string; description: string }) => {
    if (!organization?.id) return;

    try {
      const newPage: PageWritePayload = {
        name: data.name,
        description: data.description,
        type: 'dashboard',
        layout: 'grid',
        organization: organization.id || '',
        portfolio: portfolio?.id || '',
        module_type: 'dashboard',
        page_template: null,
        page_template_inputs: {},
      };

      // Add organization_id to the request payload
      const createdPage = await createPageMutation.mutateAsync({
        ...newPage,
      });

      // Update local state after successful API call
      setLocalDashboards([...localDashboards, createdPage]);

      // Navigate to the new dashboard instead of directly setting the state
      navigate(`/application/dashboards/${createdPage.id}`);

      // Refetch data to ensure we have the latest state
      query.refetch();
      setIsCreateDashboardOpen(false);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    }
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    try {
      await deletePageMutation.mutateAsync(dashboardId);

      // Update local state after successful deletion
      const updatedDashboards = localDashboards.filter((page) => page.id !== dashboardId);
      setLocalDashboards(updatedDashboards);

      // If the deleted dashboard was selected, select the first available dashboard or navigate to the dashboards list
      if (dashboardId === selectedDashboard) {
        if (updatedDashboards.length > 0 && updatedDashboards[0]?.id) {
          navigate(`/application/dashboards/${updatedDashboards[0].id}`);
        } else {
          navigate('/application/dashboards');
        }
      }

      // Refetch data to ensure we have the latest state
      query.refetch();
    } catch (error) {
      console.error('Failed to delete dashboard:', error);
    }
  };

  const confirmDeleteDashboard = () => {
    if (selectedDashboard) {
      handleDeleteDashboard(selectedDashboard);
    }
    setIsDeleteDashboardOpen(false);
  };

  const handleDeleteWidget = async (componentId: string) => {
    try {
      await deleteComponentMutation.mutateAsync({ componentId });

      // Update local state after successful deletion
      setLocalDashboards(
        localDashboards.map((page) =>
          page.id === selectedDashboard
            ? {
                ...page,
                components: page.components?.filter((comp) => comp.id !== componentId) || [],
              }
            : page,
        ),
      );
      // Refetch data to ensure we have the latest state
      query.refetch();
    } catch (error) {
      console.error('Failed to delete widget:', error);
    }
  };

  const handleEditWidget = async (updatedWidget: Component) => {
    try {
      if (!updatedWidget.id) {
        console.error('Cannot update widget without an id');
        return;
      }

      // Ensure component data is properly formatted
      const formattedWidget = {
        ...updatedWidget,
        component_data: updatedWidget.component_data?.map((data) => ({
          id: data.id || '',
          measure: data.measure,
          range_type: data.range_type,
          object_type: data.object_type,
          object_ids: data.object_ids,
          color_map: data.color_map,
          time_allocation_type: data.time_allocation_type,
        })),
      };

      // Update the component on the server
      const result = await updateComponentMutation.mutateAsync({
        componentId: updatedWidget.id,
        component: formattedWidget,
      });

      // Update local state after successful API call
      setLocalDashboards(
        localDashboards.map((page) =>
          page.id === selectedDashboard
            ? {
                ...page,
                components: page.components?.map((comp) => (comp.id === updatedWidget.id ? result : comp)) || [],
              }
            : page,
        ),
      );

      // Refetch data to ensure we have the latest state
      query.refetch();
    } catch (error) {
      console.error('Failed to update widget:', error);
    }
  };

  // Function to save the layout changes
  const handleSaveLayout = async () => {
    if (!selectedPage || !selectedPage.id || currentLayout.length === 0 || !organization?.id) return;

    try {
      setSavingLayout(true);

      // Map the current layout to components with updated layout_data
      const updatedComponents: Component[] = (selectedPage.components || []).map((comp) => {
        // Find this component's layout in currentLayout
        const layoutItem = currentLayout.find((item) => item.i === comp.id);

        return {
          ...comp,
          page: selectedPage.id,
          // Update layout_data if we found a matching layout item
          layout_data: layoutItem ? layoutToLayoutData(layoutItem) : comp.layout_data,
        } as Component;
      });

      // Map the current layout to data entry components with updated layout_data
      const updatedDataEntryComponents: DataEntryComponent[] = (selectedPage.data_entry_components || []).map(
        (comp) => {
          // Find this component's layout in currentLayout
          const layoutItem = currentLayout.find((item) => item.i === comp.id);

          return {
            ...comp,
            id: comp.id,
            page: selectedPage.id,
            // Update layout_data if we found a matching layout item
            layout_data: layoutItem ? layoutToLayoutData(layoutItem) : comp.layout_data,
          } as DataEntryComponent;
        },
      );

      // Create the updated page object
      const updatedPage: Page = {
        id: selectedPage.id,
        name: selectedPage.name,
        description: selectedPage.description,
        type: selectedPage.type,
        layout: 'grid',
        organization: organization.id,
        portfolio: portfolio?.id || '',
        module_type: 'dashboard',
        components: updatedComponents,
        page_template: selectedPage.page_template,
        data_entry_components: updatedDataEntryComponents,
      };

      // Save the updated page
      const result = await updatePageMutation.mutateAsync({ id: selectedPage.id, payload: updatedPage });

      // Update local state after successful API call
      setLocalDashboards(localDashboards.map((page) => (page.id === selectedDashboard ? result : page)));

      // Exit layout editing mode after saving
      setIsEditingLayout(false);

      // Refetch to ensure we have the latest state
      query.refetch();
    } catch (error) {
      console.error('Failed to save dashboard layout:', error);
    } finally {
      setSavingLayout(false);
    }
  };

  const handleCancelLayout = () => {
    const hasChanged = JSON.stringify(currentLayout) !== JSON.stringify(originalLayout);
    if (hasChanged && confirm('Discard all layout changes?')) {
      // Restore the original layout
      setCurrentLayout(originalLayout);

      // Force the grid to re-render with the original layout
      if (selectedPage) {
        // This will regenerate layouts from the database-stored layout_data for both regular and data entry components
        const regularLayouts = (selectedPage.components || [])
          .filter((comp) => comp.id)
          .map((comp, index) => {
            return comp.layout_data !== undefined && Object.keys(comp.layout_data).length > 0
              ? layoutDataToLayout(comp.layout_data, comp.id!)
              : createDefaultLayout(comp.id!, index);
          });

        const dataEntryLayouts = (selectedPage.data_entry_components || [])
          .filter((comp) => comp.id)
          .map((comp, index) => {
            const totalRegularWidgets = selectedPage.components?.length || 0;
            const adjustedIndex = totalRegularWidgets + index;
            return comp.layout_data !== undefined && Object.keys(comp.layout_data).length > 0
              ? layoutDataToLayout(comp.layout_data, comp.id!)
              : createDefaultLayout(comp.id!, adjustedIndex);
          });

        const allLayouts = [...regularLayouts, ...dataEntryLayouts];
        setCurrentLayout(allLayouts);
        setIsEditingLayout(false);
      }
    } else {
      setIsEditingLayout(false);
    }
  };

  // Function to reset all components to default layout
  const handleResetLayout = () => {
    if (!selectedPage) return;

    // Create default layouts for all components (both regular and data entry)
    const regularLayouts = (selectedPage.components || [])
      .filter((comp) => comp.id)
      .map((comp, index) => createDefaultLayout(comp.id!, index));

    const dataEntryLayouts = (selectedPage.data_entry_components || [])
      .filter((comp) => comp.id)
      .map((comp, index) => {
        const totalRegularWidgets = selectedPage.components?.length || 0;
        const adjustedIndex = totalRegularWidgets + index;
        return createDefaultLayout(comp.id!, adjustedIndex);
      });

    const allLayouts = [...regularLayouts, ...dataEntryLayouts];

    // Set a loading indicator
    setSavingLayout(true);

    // Use a small timeout to ensure the UI updates properly
    setCurrentLayout(allLayouts);
    setSavingLayout(false);
  };

  const handleSaveTemplateResult = (success: boolean) => {
    if (success) {
      setMessage(
        `${i18n.t('common.dashboard.dashboard')} ${i18n.t('common.dashboard.template').toLowerCase()} saved successfully!`,
      );
    } else {
      setMessage(
        `Failed to save ${i18n.t('common.dashboard.dashboard').toLowerCase()} ${i18n.t('common.dashboard.template').toLowerCase()} template.`,
      );
    }
  };

  if (query.isLoading) {
    return (
      <MenuDrivenContainer>
        <Box p="md">
          <BrandedLoadingOverlay visible={true} transitionDuration={30} variant="colored" />
        </Box>
      </MenuDrivenContainer>
    );
  }

  const handleEditDashboardModal = () => {
    setIsEditDashboardOpen(true);
  };

  const dashboardNotFound = Boolean(!selectedPage && selectedDashboard);

  return (
    <MenuDrivenContainer
      header={
        <DashboardHeader
          selectedDashboard={selectedDashboard}
          onDashboardChange={(dashboardId) => {
            navigate(`/application/dashboards/${dashboardId}`);
            setIsEditingLayout(false);
          }}
          onCreateDashboard={() => setIsCreateDashboardOpen(true)}
          onDeleteDashboard={() => setIsDeleteDashboardOpen(true)}
          dashboards={localDashboards}
        />
      }
    >
      {message && (
        <PageHeaderMessage
          message={message}
          color={message.includes('Failed') ? 'red' : 'green'}
          onClose={() => setMessage(null)}
        />
      )}
      {dashboardNotFound && (
        <DashboardInfoCard
          title={`${i18n.t('common.dashboard.dashboard')} not found`}
          objectName={i18n.t('common.dashboard.dashboard')}
          description={`Try changing your global ${i18n.t('common.portfolio').toLowerCase()} or select a different ${i18n.t('common.dashboard.dashboard').toLowerCase()}.`}
        />
      )}
      {!dashboardNotFound && (
        <Box p="md">
          <BrandedLoadingOverlay visible={savingLayout} transitionDuration={30} variant="colored" />
          <DashboardActions
            dashboardId={selectedDashboard}
            onAddDashboardWidget={() => setIsAddWidgetOpen(true)}
            onAddDataEntryWidget={() => setIsAddDataEntryWidgetOpen(true)}
            onEditDashboard={() => handleEditDashboardModal()}
            dashboardDescription={selectedPage?.description || ''}
            isEditingLayout={isEditingLayout}
            onToggleEditLayout={() => {
              if (!isEditingLayout) {
                // Save the current layout before entering edit mode
                setOriginalLayout([...currentLayout]);
              }
              setIsEditingLayout(!isEditingLayout);
            }}
            onSaveLayout={handleSaveLayout}
            onCancelLayout={handleCancelLayout}
            onResetLayout={handleResetLayout}
            onSaveTemplate={handleSaveTemplateResult}
          />

          {isEditingLayout && (
            <Flex
              justify="center"
              p="md"
              style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', marginTop: '16px' }}
            >
              <Text size="small" style={{ textAlign: 'center' }}>
                Edit Mode: Drag widgets to reposition or resize them, then click Save Layout
              </Text>
            </Flex>
          )}

          {(selectedPage?.components?.length === 0 && selectedPage?.data_entry_components?.length === 0) ||
          (!selectedPage?.components && !selectedPage?.data_entry_components) ? (
            <Flex
              justify="center"
              align="center"
              style={{
                minHeight: 'calc(100vh - 250px)',
                width: '100%',
              }}
            >
              <Card
                shadow="sm"
                padding="xl"
                radius="md"
                withBorder
                style={{
                  width: '416px',
                  height: '240px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                }}
                onClick={() => setIsAddWidgetOpen(true)}
              >
                <Flex direction="column" align="center" gap="md">
                  <Icon name="add_circle" variant="outlined" size={40} color={primaryBase} />
                  <Text weight="medium" size="large">
                    Add First Widget
                  </Text>
                </Flex>
              </Card>
            </Flex>
          ) : (
            <ResponsiveGridLayout
              key={`grid-${isEditingLayout}`}
              className="layout"
              rowHeight={120}
              style={{ width: '100%' }}
              onLayoutChange={(layout: Layout[]) => {
                // Only update the layout state if we're in editing mode or if it's the initial layout
                if (isEditingLayout || currentLayout.length === 0) {
                  setCurrentLayout(layout);
                }
              }}
              layouts={{ lg: currentLayout }}
              isDraggable={isEditingLayout}
              isResizable={isEditingLayout}
              draggableHandle=".draggable-handle"
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
              cols={{ lg: 12, md: 9, sm: 6, xs: 3 }}
              margin={[20, 40]}
              // grammar-check disable-next-line NE
              resizeHandles={['se', 'sw', 'ne', 'nw', 'e', 'w', 's']}
            >
              {selectedPage?.components?.map((component, index) => {
                if (!component.id) return null;
                component.page = selectedPage?.id || '';

                const componentLayout = calculateWidgetLayout({
                  componentId: component.id,
                  index,
                  isEditingLayout,
                  currentLayout,
                  originalLayout,
                  layoutData: component.layout_data,
                });

                return (
                  <div key={component.id} data-grid={componentLayout}>
                    <DashboardWidget
                      component={component}
                      onEdit={handleEditWidget}
                      onDelete={() => handleDeleteWidget(component.id!)}
                      isEditingLayout={isEditingLayout}
                    />
                  </div>
                );
              })}
              {selectedPage?.data_entry_components?.map((component, index) => {
                if (!component.id) return null;

                const totalRegularWidgets = selectedPage?.components?.length || 0;
                const adjustedIndex = totalRegularWidgets + index;

                const componentLayout = calculateWidgetLayout({
                  componentId: component.id,
                  index: adjustedIndex,
                  isEditingLayout,
                  currentLayout,
                  originalLayout,
                  layoutData: component.layout_data,
                });

                return (
                  <div key={component.id} data-grid={componentLayout}>
                    <DataEntryWidget
                      component={component}
                      onEdit={handleEditDataEntryWidget}
                      onDelete={() => handleDeleteDataEntryWidget(component.id!)}
                      isEditingLayout={isEditingLayout}
                    />
                  </div>
                );
              })}
            </ResponsiveGridLayout>
          )}
          <AddWidgetModal opened={isAddWidgetOpen} onClose={() => setIsAddWidgetOpen(false)} onSave={handleAddWidget} />
          <DataEntryWidgetModal
            opened={isAddDataEntryWidgetOpen}
            onClose={() => setIsAddDataEntryWidgetOpen(false)}
            onSave={handleAddDataEntryWidget}
          />
          <EditDashboardModal
            opened={isCreateDashboardOpen || isEditDashboardOpen}
            onClose={() => (isEditDashboardOpen ? setIsEditDashboardOpen(false) : setIsCreateDashboardOpen(false))}
            onCreate={handleCreateDashboard}
            onEdit={handleEditDashboard}
            initialData={{
              name: selectedPage && isEditDashboardOpen ? selectedPage.name : '',
              description: selectedPage && isEditDashboardOpen ? selectedPage.description : '',
            }}
            isCreating={isCreateDashboardOpen}
          />
          <DeleteConfirmationModal
            isOpen={isDeleteDashboardOpen}
            onClose={() => setIsDeleteDashboardOpen(false)}
            onDelete={confirmDeleteDashboard}
            title="Delete Dashboard"
            itemName={selectedPage ? selectedPage.name : ''}
            isDeleting={deletePageMutation.isPending}
            error={deletePageMutation.error}
          />
        </Box>
      )}
    </MenuDrivenContainer>
  );
};
